import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

// User interface
export interface User {
  id: string;
  email: string;
  name: string;
  // Role name, e.g. "ADMIN", "SCHOOL", "TEACHER".
  role?: string;
  schoolId?: string | null;
}

// Auth context interface
interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
}

// Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is authenticated
  const isAuthenticated = user !== null;

  // Initialize auth state (check localStorage, tokens, etc.)
  useEffect(() => {
    const initializeAuth = () => {
      try {
        // Check for stored user data (in real app, validate token with server)
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          const parsed: User = JSON.parse(storedUser);
          if (parsed.role) {
            setUser(parsed);
          } else {
            // Session saved before roles were stored: force a fresh login.
            localStorage.removeItem("user");
            localStorage.removeItem("token");
          }
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
        localStorage.removeItem("user"); // Clear invalid data
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Login function (mock implementation)
  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);

    try {
      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Mock authentication logic (replace with real API call)
      if (email === "admin@example.com" && password === "password") {
        const mockUser: User = {
          id: "1",
          email: email,
          name: "Admin User",
        };

        setUser(mockUser);
        localStorage.setItem("user", JSON.stringify(mockUser));
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error("Login error:", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    console.log("Logging out user"); // Debug log
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
